import {
    getAbsoluteMetricsToAdjust,
    getOverallAdjustedPlanData,
    getAdjustedPlanRecord,
    getAdjustedWithinPlanData,
    getEditedPlanLevelConfig,
    numericValueFormatter,
    saveMediaPlanEditConfig,
} from "@/ts/utils";
import { Popover, Button, Space, Input, Tooltip, Modal, InputRef } from "antd";
import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setGenerateMediaPlanData,
    setMediaPlanBudget,
} from "@/features/gridDataSlice";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import {
    IMediaPlanRowObj,
    IValueBoolean,
    PlanMetrics,
    TMediaPlanEditConfig,
    TMediaPlanRowData,
} from "@/ts/common-types";
import {
    ABSOLUTE_PLAN_METRICES,
    GENERATE_MEDIA_PLAN_CONFIG,
    RATIO_PLAN_METRICES,
} from "@/ts/constants";

const PlanSpendCellRenderer = (params: ICellRendererParams) => {
    const dispatch = useDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [inputValue, setInputValue] = useState(params.value);
    const [originalValue, setOriginalValue] = useState(params.value);

    const isMediaPlanEdit = useSelector(
        (state: { isMediaPlanEdit: IValueBoolean }) =>
            state.isMediaPlanEdit.value
    );

    const isMediaPlanCreate = useSelector(
        (state: { isMediaPlanCreate: IValueBoolean }) =>
            state.isMediaPlanCreate.value
    );

    const inputRef = useRef<InputRef>(null);
    const cellRef = useRef<HTMLDivElement>(null);

    const mediaPlanBudget = useSelector(
        (state: { mediaPlanBudget: { value: number } }) =>
            state.mediaPlanBudget.value
    );

    const mediaPlanData = useSelector(
        (state: { generateMediaPlanData: { value: TMediaPlanRowData } }) =>
            state.generateMediaPlanData.value
    );

    const mediaPlanEditConfig = useSelector(
        (state: { mediaPlanEditConfig: { value: TMediaPlanEditConfig } }) =>
            state.mediaPlanEditConfig.value
    );

    const handleDoubleClick = () => {
        setIsEditing(true);
        setOriginalValue(params.value);
        setInputValue(params.value);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleInputSubmit = () => {
        setIsEditing(false);
        if (inputValue !== originalValue) {
            params.node.setDataValue(PlanMetrics.SPEND, inputValue);
            setShowPopover(true);
        }
    };

    const handleInputBlur = () => {
        handleInputSubmit();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleInputSubmit();
        } else if (e.key === "Escape") {
            setInputValue(originalValue);
            setIsEditing(false);
            params.node.setDataValue(PlanMetrics.SPEND, originalValue);
        }
    };

    const makeAdjustmentWithinOverallPlan = () => {
        const {
            editedPlanLevel,
            editedPlanRecordKey,
            editedPlanRecord,
            editPlanRecordData,
        } = getEditedPlanLevelConfig(params);

        if (editPlanRecordData) {
            const percentToAdjust =
                (inputValue - editPlanRecordData.spend) /
                editPlanRecordData.spend;

            const modifiedRecord: IMediaPlanRowObj = {
                ...editPlanRecordData,
                isRowModified: true,
                spend: inputValue,
            };

            //adjust the specific record based on the spend change
            const updatedRecord = getAdjustedPlanRecord({
                recordToAdjust: modifiedRecord,
                percentToAdjust,
                absoluteMetrics: ABSOLUTE_PLAN_METRICES,
                ratioMetrics: RATIO_PLAN_METRICES,
                isIndividualSpendUpdate: true,
                recordSpend: inputValue,
            });

            const absoluteMetricesToAdjust = getAbsoluteMetricsToAdjust(
                editPlanRecordData,
                updatedRecord,
                inputValue - originalValue
            );

            //adjust all other records based on the spend change
            const { adjustmentStatus, planData, errMessage } =
                getOverallAdjustedPlanData({
                    mediaPlanData,
                    editedPlanLevel,
                    editedPlanRecordKey,
                    editedPlanRecord,
                    modifiedRecord,
                    finalUpdatedRecord: updatedRecord,
                    absoluteMetricesToAdjust,
                    isRatioEdit: false,
                });

            if (!adjustmentStatus) {
                showErrorModal(`Plan adjustment failed  - ${errMessage!}`);
                params.node.setDataValue(PlanMetrics.SPEND, originalValue);
            } else {
                saveMediaPlanEditConfig(
                    editedPlanLevel,
                    editedPlanRecord,
                    params
                );

                dispatch(
                    setMediaPlanBudget(
                        +mediaPlanBudget + (inputValue - originalValue)
                    )
                );
                dispatch(setGenerateMediaPlanData(planData));
            }
        }
    };

    const makeAdjustmentWithinPlanLevel = () => {
        const {
            editedPlanLevel,
            editedPlanRecordKey,
            editedPlanRecord,
            editPlanRecordData,
        } = getEditedPlanLevelConfig(params);

        if (editPlanRecordData) {
            const percentToAdjust =
                (inputValue - editPlanRecordData.spend) /
                editPlanRecordData.spend;

            const modifiedRecord = {
                ...editPlanRecordData,
                isRowModified: true,
                spend: inputValue,
            };

            //adjust the specific record based on the spend change
            const updatedRecord = getAdjustedPlanRecord({
                recordToAdjust: modifiedRecord,
                percentToAdjust,
                absoluteMetrics: ABSOLUTE_PLAN_METRICES,
                ratioMetrics: RATIO_PLAN_METRICES,
                isIndividualSpendUpdate: true,
                recordSpend: inputValue,
            });

            const absoluteMetricesToAdjust = getAbsoluteMetricsToAdjust(
                editPlanRecordData,
                updatedRecord,
                inputValue - originalValue
            );

            const { adjustmentStatus, planData, errMessage } =
                getAdjustedWithinPlanData({
                    mediaPlanData,
                    editedPlanLevel,
                    editedPlanRecordKey,
                    editedPlanRecord,
                    modifiedRecord,
                    finalUpdatedRecord: updatedRecord,
                    absoluteMetricesToAdjust,
                    isRatioEdit: false,
                });

            if (!adjustmentStatus) {
                showErrorModal(`Plan adjustment failed  - ${errMessage!}`);
                params.node.setDataValue(PlanMetrics.SPEND, originalValue);
            } else {
                saveMediaPlanEditConfig(
                    editedPlanLevel,
                    editedPlanRecord,
                    params
                );

                dispatch(setGenerateMediaPlanData(planData));
            }
        }
    };

    const showErrorModal = (errorMessage?: string) => {
        Modal.error({
            title: "Error",
            className: "plan-confirm-modal",
            content: errorMessage || "Something went wrong. Please try again.",
            okText: "Close",
        });
    };

    const handleModifyOverallBudget = () => {
        setShowPopover(false);
        //adjust all other metrics based on the spend change
        makeAdjustmentWithinOverallPlan();
    };

    const handleModifyOverallPlan = () => {
        setShowPopover(false);
        makeAdjustmentWithinPlanLevel();
    };

    const isModifiedCell = () => {
        const planLevel = params.columnApi
            .getAllColumns()[0]
            .getColDef().headerName;

        const editedPlanRecordKey =
            GENERATE_MEDIA_PLAN_CONFIG[planLevel]?.fieldColumn ||
            GENERATE_MEDIA_PLAN_CONFIG["Default"].fieldColumn;
        const planRecord = params.data[editedPlanRecordKey];
        return (
            mediaPlanEditConfig[planLevel]?.[planRecord]?.includes(
                PlanMetrics.SPEND
            ) ?? false
        );
    };

    const content = (
        <Space>
            <Button
                size="small"
                type="primary"
                style={{
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(24, 144, 255, 0.2)",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 10,
                }}
                onClick={handleModifyOverallBudget}
            >
                Adjust overall budget
            </Button>
            <Button
                size="small"
                style={{
                    borderRadius: "10px",
                    border: "1px solid #d9d9d9",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 10,
                }}
                onClick={handleModifyOverallPlan}
            >
                Adjust within plan
            </Button>
        </Space>
    );

    if (params.node.rowPinned)
        return numericValueFormatter(params as ValueFormatterParams);

    if (isEditing && (isMediaPlanEdit || isMediaPlanCreate)) {
        return (
            <>
                {params.node.rowPinned ? null : (
                    <Input
                        type="number"
                        ref={inputRef}
                        defaultValue={params.value}
                        onChange={(e) => setInputValue(Number(e.target.value))}
                        onBlur={handleInputBlur}
                        onKeyDown={handleKeyDown}
                        style={{ height: "100%" }}
                    />
                )}
            </>
        );
    }

    return (
        <div
            ref={cellRef}
            onDoubleClick={handleDoubleClick}
            style={{ height: "100%", padding: "4px" }}
            className={
                isModifiedCell() && !params.node.rowPinned
                    ? "modified-planning-grid-cell"
                    : isMediaPlanEdit || isMediaPlanCreate
                    ? "default-planning-grid-cell"
                    : ""
            }
        >
            <Popover
                content={content}
                open={showPopover}
                trigger="click"
                placement="right"
                destroyTooltipOnHide
                styles={{ body: { padding: "12px" } }}
                onOpenChange={(visible) => {
                    if (!visible) {
                        params.node.setDataValue(
                            PlanMetrics.SPEND,
                            originalValue
                        );
                        params.api.refreshCells({
                            rowNodes: [params.node],
                            force: true,
                        });
                        setShowPopover(false);
                    }
                }}
            >
                <div
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            params.node.setDataValue(
                                PlanMetrics.SPEND,
                                originalValue
                            );
                            params.api.refreshCells({
                                rowNodes: [params.node],
                                force: true,
                            });
                            setShowPopover(false);
                        }
                    }}
                    className={
                        isModifiedCell()
                            ? "modified-planning-grid-cell"
                            : !isFinite(params.value)
                            ? "invalid-planning-grid-cell"
                            : isMediaPlanEdit || isMediaPlanCreate
                            ? "default-planning-grid-cell"
                            : ""
                    }
                >
                    {numericValueFormatter(params as ValueFormatterParams)}
                </div>
            </Popover>
        </div>
    );
};

export default PlanSpendCellRenderer;
